import React, {useCallback, useEffect, useState, useRef} from 'react';
import type {AstView} from 'rendition-core';
import classNames from 'classnames';
import {PrismAsync as SyntaxHighlighter} from 'react-syntax-highlighter';
import {synthwave84} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {useDocumentContext} from 'Contexts/DocumentContext';
import Button from 'Components/Tailwind/Button';
import {partialCompileMultiFile} from '../../../Common/api';
import {copyToClipBoard} from '../../../utils/CopyHelpers';

const OFF = 'off';
const SMALL = 'small';
const LARGE = 'large';

type PreviewPosition = typeof OFF | typeof SMALL | typeof LARGE;

const SMALL_OFFSET = '75vh';

const LARGE_OFFSET = '60vh';

type CodeResult = {
    type: string;
    code: string;
};

export function CopyTailwindControls({view, selectedId}: {view: AstView; selectedId: string}) {
    const [uiState, setUiState] = useState<'idle' | 'loading' | 'completed' | 'error'>('idle');
    const {documentId} = useDocumentContext();
    const [previewPosition, setPreviewPosition] = useState<PreviewPosition>(OFF);

    const [codeWithChildren, setCodeWithChildren] = useState<CodeResult[]>([]);
    const [code, setCode] = useState<CodeResult[]>([]);

    useEffect(() => {
        const effect = async () => {
            if (selectedId) {
                setUiState('loading');
                try {
                    const noChildrenCode = await partialCompileMultiFile(view, selectedId, false);
                    setCode(noChildrenCode.data);
                    const childCodeResult = await partialCompileMultiFile(view, selectedId, true);
                    setCodeWithChildren(childCodeResult.data);
                    setUiState('idle');
                } catch (e) {
                    setUiState('error');
                }
            } else {
                setCode([]);
            }
        };
        effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, view.meta.compilerOptions.tailWind, view.meta.framework]);

    const copySection = useCallback(async (codeToCopy: string) => {
        copyToClipBoard(codeToCopy, 'text');
        setUiState('completed');
        setTimeout(() => {
            setUiState('idle');
        }, 1000);
    }, []);

    const btnContainerRef = useRef<HTMLDivElement>(null);
    const codePreviewRef = useRef<HTMLDivElement>(null);
    const hoverTargetRef = useRef<HTMLDivElement>(null);

    const onMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const isWindow = e.relatedTarget === window;
        if (isWindow) {
            setPreviewPosition(OFF);
            return;
        }

        if (
            e.relatedTarget === codePreviewRef.current ||
            codePreviewRef.current?.contains(e.relatedTarget as Node) ||
            e.relatedTarget === btnContainerRef.current ||
            btnContainerRef.current?.contains(e.relatedTarget as Node) ||
            e.relatedTarget === hoverTargetRef.current
        ) {
            return;
        }
        setPreviewPosition(OFF);
    }, []);

    const aiComponentsClick = () => {
        window.open(`/aigen?docId=${documentId}&nodeId=${selectedId}`, '_blank')?.focus();
    };

    return (
        <div className="relative flex w-full gap-3" id="rendition-copy-tailwind">
            <CodePreview
                position={previewPosition}
                code={
                    previewPosition === OFF ? '' : previewPosition === LARGE ? codeWithChildren[0]?.code : code[0]?.code
                }
                setPreviewPosition={setPreviewPosition}
                codePreviewRef={codePreviewRef}
                buttonContainerRef={btnContainerRef}
                hoverTargetRef={hoverTargetRef}
            />
            <div
                ref={btnContainerRef}
                onMouseLeave={onMouseLeave}
                className={classNames(
                    'border-gray flex flex-col items-center justify-start gap-2 rounded-tl-lg rounded-tr-lg bg-zinc-900 px-4 py-3',
                    'absolute bottom-0 right-2 z-[999] flex w-80',
                    (previewPosition === LARGE || previewPosition === SMALL) && 'mt-16'
                )}
            >
                <Button
                    className={classNames('flex w-full basis-full gap-2 rounded-lg')}
                    onMouseEnter={() => setPreviewPosition('large')}
                    outlined
                    disabled={uiState === 'loading' || !selectedId}
                    onClick={() => copySection(codeWithChildren[0].code)}
                >
                    <CodeIcon />
                    {(uiState === 'idle' || uiState === 'loading') && 'Copy '}
                    {uiState === 'error' && 'Error '}
                    {uiState === 'completed' && 'Copied! '}
                </Button>
                <Button
                    className={classNames('flex w-full basis-full gap-2 rounded-lg')}
                    outlined
                    disabled={uiState === 'loading' || !selectedId}
                    onClick={() => copySection(code[0].code)}
                    onMouseEnter={() => setPreviewPosition('small')}
                >
                    <ElementIcon />

                    {(uiState === 'idle' || uiState === 'loading') && 'Copy Element'}
                    {uiState === 'error' && 'Error '}
                    {uiState === 'completed' && 'Copied! '}
                </Button>
                {view.meta.compilerOptions.tailWind && (
                    <Button
                        className={classNames(
                            'flex w-full basis-full gap-2 rounded-lg border-transparent bg-[linear-gradient(91deg,_#00a3ff_-3%,#d600da_117%)]'
                        )}
                        outlined
                        disabled={uiState === 'loading' || !selectedId}
                        onClick={aiComponentsClick}
                    >
                        <ElementIcon />
                        AI Components (Alpha)
                    </Button>
                )}

                {previewPosition !== OFF && (
                    <div
                        onMouseLeave={onMouseLeave}
                        ref={hoverTargetRef}
                        className="absolute -left-32 -top-32 bottom-0 pr-32"
                    ></div>
                )}
            </div>
        </div>
    );
}
const ElementIcon = () => (
    <svg width="21" height="13" viewBox="0 0 21 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M1.18398 7.75971C0.899436 7.62172 0.71875 7.33329 0.71875 7.01706V7.01706C0.71875 6.70082 0.899437 6.41239 1.18398 6.2744L5.9463 3.96497C6.36388 3.76246 6.84943 4.06663 6.84943 4.53073V4.53073C6.84943 4.777 6.70566 5.0006 6.48159 5.10281L2.35914 6.98315C2.34786 6.98829 2.33638 6.9764 2.34193 6.96531V6.96531C2.34813 6.9529 2.36683 6.95731 2.36683 6.97119V7.06264C2.36683 7.07594 2.34902 7.08037 2.3428 7.06861V7.06861C2.33711 7.05785 2.34828 7.04599 2.35936 7.05103L6.48 8.92634C6.705 9.02874 6.84943 9.25316 6.84943 9.50036V9.50036C6.84943 9.96586 6.36242 10.2709 5.94358 10.0678L1.18398 7.75971Z"
            fill="white"
        />
        <path
            d="M11.8643 0.84021C12.2559 0.84021 12.5408 1.21188 12.439 1.59001L9.71248 11.7194C9.64253 11.9792 9.40692 12.1598 9.13781 12.1598V12.1598C8.74623 12.1598 8.46137 11.7881 8.56315 11.41L11.2897 1.28065C11.3596 1.02079 11.5952 0.84021 11.8643 0.84021V0.84021Z"
            fill="white"
        />
        <path
            d="M20.2811 7.01706C20.2811 7.33329 20.1004 7.62172 19.8158 7.75971L15.0562 10.0678C14.6374 10.2709 14.1504 9.96586 14.1504 9.50036V9.50036C14.1504 9.25316 14.2948 9.02874 14.5198 8.92634L18.6362 7.05298C18.648 7.04759 18.6598 7.06064 18.6532 7.07185V7.07185C18.6463 7.08355 18.6284 7.07867 18.6284 7.0651V6.96867C18.6284 6.95446 18.6473 6.94955 18.6542 6.96198V6.96198C18.6607 6.97361 18.6485 6.98673 18.6364 6.98121L14.5182 5.10281C14.2942 5.0006 14.1504 4.777 14.1504 4.53073V4.53073C14.1504 4.06663 14.6359 3.76246 15.0535 3.96497L19.8158 6.2744C20.1004 6.41239 20.2811 6.70082 20.2811 7.01706V7.01706Z"
            fill="white"
        />
    </svg>
);
const CodeIcon = () => (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M1.01709 8.23804H8.16455C8.32536 8.23804 8.46077 8.18302 8.5708 8.073C8.68083 7.95874 8.73584 7.82121 8.73584 7.6604C8.73584 7.50382 8.68083 7.36841 8.5708 7.25415C8.46077 7.13989 8.32536 7.08276 8.16455 7.08276H1.01709C0.860514 7.08276 0.727214 7.13989 0.617188 7.25415C0.507161 7.36841 0.452148 7.50382 0.452148 7.6604C0.452148 7.81698 0.507161 7.95239 0.617188 8.06665C0.727214 8.18091 0.860514 8.23804 1.01709 8.23804ZM10.2783 8.23804H12.9697C13.1305 8.23804 13.266 8.18302 13.376 8.073C13.4902 7.95874 13.5474 7.82121 13.5474 7.6604C13.5474 7.50382 13.4902 7.36841 13.376 7.25415C13.266 7.13989 13.1305 7.08276 12.9697 7.08276H10.2783C10.1217 7.08276 9.98844 7.13989 9.87842 7.25415C9.76839 7.36841 9.71338 7.50382 9.71338 7.6604C9.71338 7.81698 9.76839 7.95239 9.87842 8.06665C9.98844 8.18091 10.1217 8.23804 10.2783 8.23804ZM1.02344 10.8967H3.71484C3.87565 10.8967 4.01107 10.8396 4.12109 10.7253C4.23112 10.6111 4.28613 10.4736 4.28613 10.3127C4.28613 10.1562 4.23112 10.0229 4.12109 9.91284C4.01107 9.79858 3.87565 9.74146 3.71484 9.74146H1.02344C0.866862 9.74146 0.731445 9.79647 0.617188 9.90649C0.507161 10.0165 0.452148 10.1519 0.452148 10.3127C0.452148 10.4736 0.507161 10.6111 0.617188 10.7253C0.731445 10.8396 0.866862 10.8967 1.02344 10.8967ZM5.83496 10.8967H12.9761C13.1326 10.8967 13.266 10.8396 13.376 10.7253C13.4902 10.6111 13.5474 10.4736 13.5474 10.3127C13.5474 10.1562 13.4924 10.0229 13.3823 9.91284C13.2723 9.79858 13.1369 9.74146 12.9761 9.74146H5.83496C5.66992 9.74146 5.53239 9.79647 5.42236 9.90649C5.31234 10.0165 5.25732 10.1519 5.25732 10.3127C5.25732 10.4736 5.31234 10.6111 5.42236 10.7253C5.53662 10.8396 5.67415 10.8967 5.83496 10.8967ZM1.01709 14.2166H7.70752C7.8641 14.2166 7.9974 14.1594 8.10742 14.0452C8.22168 13.9351 8.27881 13.7997 8.27881 13.6389C8.27881 13.4781 8.22168 13.3427 8.10742 13.2327C7.9974 13.1184 7.8641 13.0613 7.70752 13.0613H1.01709C0.860514 13.0613 0.727214 13.1184 0.617188 13.2327C0.507161 13.3469 0.452148 13.4823 0.452148 13.6389C0.452148 13.7997 0.507161 13.9351 0.617188 14.0452C0.727214 14.1594 0.860514 14.2166 1.01709 14.2166Z"
            fill="white"
        />
        <path
            d="M3.71484 4.93872H1.02344C0.866862 4.93872 0.731445 4.88159 0.617188 4.76733C0.507161 4.65308 0.452148 4.51554 0.452148 4.35474C0.452148 4.19393 0.507161 4.05851 0.617188 3.94849C0.731445 3.83846 0.866862 3.78345 1.02344 3.78345H3.71484C3.87565 3.78345 4.01107 3.84058 4.12109 3.95483C4.23112 4.06486 4.28613 4.19816 4.28613 4.35474C4.28613 4.51554 4.23112 4.65308 4.12109 4.76733C4.01107 4.88159 3.87565 4.93872 3.71484 4.93872Z"
            fill="white"
        />
        <path
            d="M12.9761 4.93872H5.83496C5.67415 4.93872 5.53662 4.88159 5.42236 4.76733C5.31234 4.65308 5.25732 4.51554 5.25732 4.35474C5.25732 4.19393 5.31234 4.05851 5.42236 3.94849C5.53239 3.83846 5.66992 3.78345 5.83496 3.78345H12.9761C13.1369 3.78345 13.2723 3.84058 13.3823 3.95483C13.4924 4.06486 13.5474 4.19816 13.5474 4.35474C13.5474 4.51554 13.4902 4.65308 13.376 4.76733C13.266 4.88159 13.1326 4.93872 12.9761 4.93872Z"
            fill="white"
        />
        <path
            d="M7.70752 1.93872H1.01709C0.860514 1.93872 0.727214 1.88159 0.617188 1.76733C0.507161 1.65731 0.452148 1.52189 0.452148 1.36108C0.452148 1.20451 0.507161 1.06909 0.617188 0.954834C0.727214 0.840576 0.860514 0.783447 1.01709 0.783447H7.70752C7.8641 0.783447 7.9974 0.840576 8.10742 0.954834C8.22168 1.06486 8.27881 1.20028 8.27881 1.36108C8.27881 1.52189 8.22168 1.65731 8.10742 1.76733C7.9974 1.88159 7.8641 1.93872 7.70752 1.93872Z"
            fill="white"
        />
    </svg>
);

interface PreviewProps {
    code: string;
    position: PreviewPosition;
    setPreviewPosition: (position: PreviewPosition) => void;
    buttonContainerRef: React.RefObject<HTMLDivElement>;
    codePreviewRef: React.RefObject<HTMLDivElement>;
    hoverTargetRef: React.RefObject<HTMLDivElement>;
}

function CodePreview({
    code,
    hoverTargetRef,
    setPreviewPosition,
    buttonContainerRef,
    codePreviewRef,
    position,
}: PreviewProps) {
    return (
        <div
            ref={codePreviewRef}
            onMouseLeave={(e) => {
                const isWindow = e.relatedTarget === window;
                if (isWindow) {
                    setPreviewPosition(OFF);
                    return;
                }

                if (
                    e.relatedTarget === buttonContainerRef.current ||
                    buttonContainerRef.current?.contains(e.relatedTarget as Node) ||
                    e.relatedTarget === hoverTargetRef.current
                ) {
                    return;
                }

                setPreviewPosition(OFF);
            }}
            style={{
                top: code ? (position === LARGE ? LARGE_OFFSET : SMALL_OFFSET) : `100vh`,
            }}
            className={classNames({
                'fixed left-[260px] z-[998] flex justify-center transition-all duration-500 ease-in-out': true,
                'pr-32': true,
                'h-[40vh]': position === LARGE,
                'h-[25vh]': position === SMALL,
            })}
        >
            <div className="color-white-100 flex h-full w-[calc(100vw-600px)] flex-col rounded-md bg-zinc-800 pb-3 pl-4 pr-4 pt-2">
                <h1 className="p-2 text-sm font-bold text-white">Code</h1>
                <div className="ml-px h-full w-full self-stretch overflow-y-auto rounded-md bg-neutral-950">
                    <SyntaxHighlighter
                        customStyle={{
                            margin: 0,
                            borderRadius: '5px',
                            backgroundColor: 'transparent',
                            fontSize: '12px',
                            paddingBottom: '20px',
                            height: '100%',
                        }}
                        language="jsx"
                        style={synthwave84}
                    >
                        {code}
                    </SyntaxHighlighter>
                </div>
            </div>
        </div>
    );
}
